import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

import {
  BusinessIdFailureReason,
  validateBusinessId,
  validateClientBusinessId,
  validateEndCustomerBusinessId,
} from './business-id';

/**
 * The only Angular-coupled part of the business-id validator. Thin synchronous
 * `ValidatorFn` adapters over the portable module in `business-id.ts`; the pure
 * module ports to Svelte unchanged.
 *
 * An empty control is left to `Validators.required` (returns null here), so the
 * two concerns don't both light up. An invalid non-empty value maps to
 * `{ businessId: { reason } }` — the error key stays `businessId` so existing
 * templates keep working.
 */

function toError(reason: BusinessIdFailureReason | null): ValidationErrors {
  return { businessId: { reason } };
}

/** Strict policy: the value must be a checksum-valid FI or EE business id. */
export function clientBusinessIdValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || value === '') {
      return null;
    }
    const result = validateClientBusinessId(value);
    return result.valid ? null : toError(result.reason);
  };
}

/** End-customer policy: FI/EE checksums enforced, foreign ASCII ids pass. */
export function endCustomerBusinessIdValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value == null || value === '') {
      return null;
    }
    const result = validateEndCustomerBusinessId(value);
    return result.valid ? null : toError(result.reason);
  };
}

/**
 * Canonicalize an operator-entered business-id control in place, so the value
 * submitted to the backend matches the strict write boundary and the operator
 * sees what will be stored. No-op on a disabled or pristine control: a disabled
 * control carries a stored value the operator cannot edit, and a pristine
 * control was populated from stored data rather than typed. Canonicalizing
 * either manufactures a byte-for-byte "change" the backend then rejects
 * (end-customer immutability, client validate-on-change, the client unique
 * index) — the exact failure class this change set out to remove (design D10).
 * Only rewrites when the value actually differs from its canonical form.
 */
export function canonicalizeBusinessIdControl(control: AbstractControl): void {
  if (control.disabled || control.pristine) {
    return;
  }
  const value = control.value;
  if (value == null || value === '') {
    return;
  }
  const canonical = validateBusinessId(value).canonical;
  if (canonical != null && canonical !== value) {
    control.setValue(canonical);
  }
}
